import type { ReactNode } from "react";
import clsx from "clsx";
import { ToggleButton } from "react-aria-components";

import Icon from "./icon";

import styles from "./toggle-chip.module.css";

export type ToggleChipProps = {
  /**
   * Plain text. The check glyph is the component's business, not the
   * caller's — which is why this is a string and not a `ReactNode`.
   */
  label: string;
  /**
   * Replaces the check slot with a caller-supplied node — the avatar case,
   * where the colour is computed per user and the component cannot derive it.
   * It is the check *or* the adornment, never both, and it is deliberately not
   * `children`: that would lose the fixed-slot guarantee below.
   */
  adornment?: ReactNode;
  /** Controlled. */
  isSelected?: boolean;
  /** Uncontrolled. */
  defaultSelected?: boolean;
  /**
   * Note the argument: react-aria hands you the **new** selected state. A
   * caller that toggles a set should use it rather than re-reading its own
   * state.
   */
  onChange?: (isSelected: boolean) => void;
  isDisabled?: boolean;
  // NO className, consistent with Button: a chip that needs one is a missing
  // variant here.
};

/**
 * A pill that toggles.
 *
 * Multi-select filters and membership chips — "is this zone in the group?" —
 * where each pill carries its own on/off state. For single-select use a
 * `RadioGroup`; for a non-interactive label use `Tag`; for a chip that fires an
 * action rather than holding a state use `ActionChip`.
 *
 * Selection is styled off react-aria's `[data-selected]`, and `aria-pressed`
 * comes from `ToggleButton` — so a toggled chip announces itself without the
 * call site saying anything.
 */
function ToggleChip({
  label,
  adornment,
  isSelected,
  defaultSelected,
  onChange,
  isDisabled,
}: ToggleChipProps) {
  return (
    <ToggleButton
      className={clsx(styles.chip, {
        [styles.withAdornment]: adornment !== undefined,
      })}
      isSelected={isSelected}
      defaultSelected={defaultSelected}
      onChange={onChange}
      isDisabled={isDisabled}
    >
      {({ isSelected: selected }) => (
        <>
          {adornment ?? (
            <span className={styles.checkSlot}>
              {selected ? <Icon name="Check" size={16} /> : null}
            </span>
          )}
          {label}
        </>
      )}
    </ToggleButton>
  );
}

export default ToggleChip;
